class FadeHeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-fade-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-fade-dot]'));
    this.dragSurface = this.querySelector('[data-fade-drag]');
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.interval = Number(this.dataset.interval) || 5000;
    this.paused = false;
    this.pointerInside = false;
    this.focusInside = false;
    this.dragState = null;
    this.dragFrame = null;
    this.settleFrame = null;
    this.settleAnimations = [];
    this.isSettling = false;
    this.suppressClick = false;
    this.useTouchEvents = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-fade-prev]')?.addEventListener('click', () => this.show(this.currentIndex - 1, true), { signal });
    this.querySelector('[data-fade-next]')?.addEventListener('click', () => this.show(this.currentIndex + 1, true), { signal });
    this.querySelector('[data-fade-toggle]')?.addEventListener('click', () => this.toggleAutoplay(), { signal });
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true), { signal }));

    this.addEventListener('mouseenter', () => {
      this.pointerInside = true;
      this.stopTimer();
    }, { signal });
    this.addEventListener('mouseleave', () => {
      this.pointerInside = false;
      if (!this.dragState) this.startTimer();
    }, { signal });
    this.addEventListener('focusin', () => {
      this.focusInside = true;
      this.stopTimer();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focusInside = false;
        if (!this.dragState) this.startTimer();
      }
    }, { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-fade-slide]');
      if (slide) this.show(this.slides.indexOf(slide), false);
      this.stopTimer();
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => this.startTimer(), { signal });

    if (this.dragSurface && this.slides.length > 1) {
      this.dragSurface.addEventListener('pointerdown', (event) => this.onPointerDown(event), { passive: true, signal });
      this.dragSurface.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: true, signal });
      this.dragSurface.addEventListener('pointerup', (event) => this.onPointerEnd(event), { passive: true, signal });
      this.dragSurface.addEventListener('pointercancel', (event) => this.onPointerEnd(event, true), { passive: true, signal });
      if (this.useTouchEvents) {
        this.dragSurface.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
        this.dragSurface.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
        this.dragSurface.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true, signal });
        this.dragSurface.addEventListener('touchcancel', (event) => this.onTouchEnd(event, true), { passive: true, signal });
      }
      this.dragSurface.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.addEventListener('click', (event) => this.onClickCapture(event), { capture: true, signal });
    }

    this.show(this.currentIndex, false);
    this.startTimer();
  }

  disconnectedCallback() {
    this.stopTimer();
    window.cancelAnimationFrame(this.dragFrame);
    window.cancelAnimationFrame(this.settleFrame);
    window.clearTimeout(this.clickSuppressionTimer);
    window.clearTimeout(this.settleTimer);
    this.clearDragLayout();
    this.abortController?.abort();
  }

  show(index, resetTimer = false) {
    if (this.isSettling) this.completeSettle(false);
    this.setActiveIndex(index);
    if (resetTimer) this.restartTimer();
  }

  setActiveIndex(index) {
    if (this.slides.length === 0) return;
    this.currentIndex = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentIndex;
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === this.currentIndex)));
    this.preloadAdjacentSlides();
  }

  preloadAdjacentSlides() {
    if (this.slides.length < 2) return;

    const previousIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    const nextIndex = (this.currentIndex + 1) % this.slides.length;

    [previousIndex, nextIndex].forEach((index) => {
      this.slides[index].querySelectorAll('img').forEach((image) => {
        if (image.dataset.fadeHeroPreloaded === 'true') return;
        image.dataset.fadeHeroPreloaded = 'true';
        image.loading = 'eager';
        image.decoding = 'async';
        const decodePromise = image.decode?.();
        decodePromise?.catch(() => {});
      });
    });
  }

  onPointerDown(event) {
    if (this.useTouchEvents && event.pointerType === 'touch') return;

    this.beginDrag({
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      clientX: event.clientX,
      clientY: event.clientY,
      isPrimary: event.isPrimary,
      button: event.button,
    });
  }

  onTouchStart(event) {
    if (event.targetTouches.length !== 1) return;

    const touch = event.targetTouches[0];
    const edgeThreshold = 20;
    if (touch.clientX <= edgeThreshold || touch.clientX >= window.innerWidth - edgeThreshold) return;

    this.beginDrag({
      pointerId: touch.identifier,
      pointerType: 'touch',
      clientX: touch.clientX,
      clientY: touch.clientY,
      isPrimary: true,
      button: 0,
    });
  }

  beginDrag({ pointerId, pointerType, clientX, clientY, isPrimary, button }) {
    if (
      this.dragState
      || this.isSettling
      || isPrimary === false
      || (pointerType === 'mouse' && button !== 0)
    ) return;

    const width = this.dragSurface.getBoundingClientRect().width || this.dragSurface.clientWidth;
    if (!width) return;

    const now = performance.now();

    this.dragState = {
      pointerId,
      pointerType,
      startX: clientX,
      startY: clientY,
      lastX: clientX,
      lastMovedAt: now,
      velocityX: 0,
      width,
      axis: null,
      targetDirection: 0,
      targetIndex: this.currentIndex,
      targetSlide: null,
    };
    if (pointerType === 'touch') this.primeDragSlides();
    this.stopTimer();
  }

  onPointerMove(event) {
    if (this.useTouchEvents && event.pointerType === 'touch') return;

    const drag = this.dragState;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const previousAxis = drag.axis;
    this.updateDrag(event.pointerId, event.clientX, event.clientY);

    if (previousAxis !== 'horizontal' && drag.axis === 'horizontal') {
      try {
        this.dragSurface.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Pointer capture can fail if the pointer has already been cancelled.
      }
    }
  }

  onTouchMove(event) {
    const drag = this.dragState;
    if (!drag || drag.pointerType !== 'touch') return;

    const touch = this.findTouch(event.changedTouches, drag.pointerId);
    if (!touch) return;

    this.updateDrag(drag.pointerId, touch.clientX, touch.clientY);
    if (drag.axis === 'horizontal' && event.cancelable) event.preventDefault();
  }

  updateDrag(pointerId, clientX, clientY) {
    const drag = this.dragState;
    if (!drag || drag.pointerId !== pointerId) return;

    const now = performance.now();
    const deltaX = clientX - drag.startX;
    const deltaY = clientY - drag.startY;
    const moveElapsed = now - drag.lastMovedAt;

    if (moveElapsed > 0) drag.velocityX = (clientX - drag.lastX) / moveElapsed;
    drag.lastX = clientX;
    drag.lastMovedAt = now;

    if (!drag.axis) {
      if (Math.abs(deltaX) >= 2 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.classList.add('is-drag-active');
        this.prepareDragSlides(drag, deltaX < 0 ? 1 : -1);
      }

      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
      drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      if (drag.axis === 'horizontal') {
        this.classList.add('is-drag-active', 'is-dragging');
        this.renderDragLayout(drag, deltaX);
        return;
      }

      this.clearDragLayout();
    }

    if (drag.axis !== 'horizontal') return;

    this.renderDragLayout(drag, deltaX);
  }

  onPointerEnd(event, cancelled = false) {
    if (this.useTouchEvents && event.pointerType === 'touch') return;

    const drag = this.dragState;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const endX = !cancelled && Number.isFinite(event.clientX) ? event.clientX : drag.lastX;
    this.finishDrag(event.pointerId, endX, cancelled);

    try {
      if (this.dragSurface.hasPointerCapture(event.pointerId)) this.dragSurface.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // The browser may release capture before pointercancel is dispatched.
    }
  }

  onTouchEnd(event, cancelled = false) {
    const drag = this.dragState;
    if (!drag || drag.pointerType !== 'touch') return;

    const touch = this.findTouch(event.changedTouches, drag.pointerId);
    if (!touch && !cancelled) return;

    this.finishDrag(drag.pointerId, touch?.clientX ?? drag.lastX, cancelled);
  }

  finishDrag(pointerId, clientX, cancelled = false) {
    const drag = this.dragState;
    if (!drag || drag.pointerId !== pointerId) return;

    const deltaX = Math.max(-drag.width, Math.min(drag.width, clientX - drag.startX));
    const movementAge = performance.now() - drag.lastMovedAt;
    const velocityX = movementAge <= 100 ? drag.velocityX : 0;
    const distanceThreshold = Math.min(160, Math.max(56, drag.width * 0.18));
    const isQuickSwipe = Math.abs(deltaX) >= 18 && Math.abs(velocityX) >= 0.45;
    const shouldChangeSlide = !cancelled
      && drag.axis === 'horizontal'
      && (Math.abs(deltaX) >= distanceThreshold || isQuickSwipe);

    this.suppressClick = !cancelled && drag.axis === 'horizontal' && Math.abs(deltaX) >= 6;

    if (drag.axis === 'horizontal') this.flushDragLayout(drag, deltaX);

    if (drag.axis === 'horizontal') {
      const direction = shouldChangeSlide ? (deltaX < 0 ? 1 : -1) : (drag.targetDirection || 1);
      this.prepareDragSlides(drag, direction);
      this.settleDrag(
        drag,
        shouldChangeSlide ? drag.targetIndex : this.currentIndex,
        shouldChangeSlide ? -direction * drag.width : 0,
        deltaX
      );
    } else {
      this.dragState = null;
      this.clearDragLayout();
      this.resumeAfterInteraction(drag.pointerType);
    }

    if (this.suppressClick) {
      window.clearTimeout(this.clickSuppressionTimer);
      this.clickSuppressionTimer = window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }
  }

  findTouch(touchList, identifier) {
    for (let index = 0; index < touchList.length; index += 1) {
      if (touchList[index].identifier === identifier) return touchList[index];
    }
    return null;
  }

  onClickCapture(event) {
    if (!this.suppressClick) return;

    this.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
  }

  renderDragLayout(drag, deltaX) {
    // iOS touch events are already delivered near the display refresh boundary.
    // Applying immediately avoids adding one frame of finger-to-slide latency.
    if (drag.pointerType === 'touch') {
      this.applyDragLayout(drag, deltaX);
      return;
    }

    this.scheduleDragLayout(drag, deltaX);
  }

  scheduleDragLayout(drag, deltaX) {
    drag.pendingOffset = Math.max(-drag.width, Math.min(drag.width, deltaX));
    if (this.dragFrame !== null) return;

    this.dragFrame = window.requestAnimationFrame(() => {
      this.dragFrame = null;
      if (this.dragState !== drag) return;
      this.applyDragLayout(drag, drag.pendingOffset);
    });
  }

  flushDragLayout(drag, deltaX) {
    if (this.dragFrame !== null) {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
    }

    this.applyDragLayout(drag, Math.max(-drag.width, Math.min(drag.width, deltaX)));
  }

  applyDragLayout(drag, deltaX) {
    const clampedOffset = Math.max(-drag.width, Math.min(drag.width, deltaX));
    const direction = clampedOffset === 0 ? (drag.targetDirection || 1) : (clampedOffset < 0 ? 1 : -1);
    const renderedOffset = this.alignToDevicePixel(clampedOffset);

    this.prepareDragSlides(drag, direction);
    this.dragCurrentSlide.style.transform = `translate3d(${renderedOffset}px, 0, 0)`;
    drag.targetSlide.style.transform = `translate3d(${this.alignToDevicePixel(drag.targetOrigin + clampedOffset)}px, 0, 0)`;
  }

  alignToDevicePixel(offset) {
    const pixelRatio = window.devicePixelRatio || 1;
    return Math.round(offset * pixelRatio) / pixelRatio;
  }

  primeDragSlides() {
    const currentSlide = this.slides[this.currentIndex];
    if (!currentSlide) return;

    this.classList.add('is-drag-preparing');
    currentSlide.classList.add('is-drag-participant');
    currentSlide.style.transform = 'translate3d(0, 0, 0)';
    this.dragCurrentSlide = currentSlide;
  }

  prepareDragSlides(drag, direction) {
    if (drag.targetDirection === direction && drag.targetSlide) return;

    const currentSlide = this.slides[this.currentIndex];
    const targetIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    const targetSlide = this.slides[targetIndex];

    if (drag.targetSlide && drag.targetSlide !== targetSlide) {
      drag.targetSlide.classList.remove('is-drag-participant');
      this.clearDragSlideStyles(drag.targetSlide);
    }

    currentSlide.classList.add('is-drag-participant');
    currentSlide.style.transform ||= 'translate3d(0, 0, 0)';
    targetSlide.classList.add('is-drag-participant');
    targetSlide.style.transform = `translate3d(${direction * drag.width}px, 0, 0)`;

    drag.targetDirection = direction;
    drag.targetIndex = targetIndex;
    drag.targetSlide = targetSlide;
    drag.targetOrigin = direction * drag.width;
    this.dragCurrentSlide = currentSlide;
  }

  settleDrag(drag, targetIndex, targetOffset, currentOffset) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const remainingRatio = Math.min(1, Math.abs(targetOffset - currentOffset) / drag.width);
    const duration = reducedMotion ? 0 : Math.round(Math.max(160, Math.min(300, remainingRatio * 280)));
    const isReturning = targetIndex === this.currentIndex;
    const currentToOpacity = isReturning ? 1 : 0;
    const targetToOpacity = isReturning ? 0 : 1;
    const targetFromOffset = drag.targetOrigin + currentOffset;
    const targetToOffset = drag.targetOrigin + targetOffset;

    this.dragState = null;
    this.isSettling = true;
    this.settleTargetIndex = targetIndex;
    this.settlePointerType = drag.pointerType;
    this.style.setProperty('--fade-hero-settle-duration', `${duration}ms`);
    this.classList.add('is-drag-active');
    this.classList.remove('is-dragging');
    this.classList.add('is-settling');

    if (duration === 0) {
      this.applyDragLayout(drag, targetOffset);
      this.completeSettle();
      return;
    }

    const animationOptions = {
      duration,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards',
    };

    if (typeof this.dragCurrentSlide.animate !== 'function' || typeof drag.targetSlide.animate !== 'function') {
      this.applyDragLayout(drag, targetOffset);
      this.dragCurrentSlide.style.opacity = String(currentToOpacity);
      drag.targetSlide.style.opacity = String(targetToOpacity);
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => this.completeSettle(), duration);
      return;
    }

    this.settleAnimations = [
      this.dragCurrentSlide.animate([
        { transform: `translate3d(${currentOffset}px, 0, 0)`, opacity: 1 },
        { transform: `translate3d(${targetOffset}px, 0, 0)`, opacity: currentToOpacity },
      ], animationOptions),
      drag.targetSlide.animate([
        { transform: `translate3d(${targetFromOffset}px, 0, 0)`, opacity: 1 },
        { transform: `translate3d(${targetToOffset}px, 0, 0)`, opacity: targetToOpacity },
      ], animationOptions),
    ];

    Promise.all(this.settleAnimations.map((animation) => animation.finished))
      .then(() => this.completeSettle())
      .catch(() => {});
    window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => this.completeSettle(), duration + 80);
  }

  completeSettle(resumeAutoplay = true) {
    if (!this.isSettling) return;

    const targetIndex = this.settleTargetIndex;
    const pointerType = this.settlePointerType;
    window.clearTimeout(this.settleTimer);
    this.isSettling = false;
    this.setActiveIndex(targetIndex);
    this.clearDragLayout();

    if (resumeAutoplay) this.resumeAfterInteraction(pointerType);
  }

  clearDragLayout() {
    window.cancelAnimationFrame(this.dragFrame);
    window.cancelAnimationFrame(this.settleFrame);
    this.settleAnimations.forEach((animation) => animation.cancel());
    this.settleAnimations = [];
    this.dragFrame = null;
    this.settleFrame = null;
    this.classList.remove('is-drag-preparing', 'is-drag-active', 'is-dragging', 'is-settling');
    this.style.removeProperty('--fade-hero-settle-duration');
    this.slides?.forEach((slide) => {
      slide.classList.remove('is-drag-participant');
      this.clearDragSlideStyles(slide);
    });
    this.dragCurrentSlide = null;
    this.settleTargetIndex = null;
    this.settlePointerType = null;
  }

  clearDragSlideStyles(slide) {
    slide?.style.removeProperty('transform');
    slide?.style.removeProperty('opacity');
  }

  resumeAfterInteraction(pointerType) {
    if (pointerType !== 'mouse' || (!this.pointerInside && !this.focusInside)) this.restartTimer();
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') this.show(this.currentIndex - 1, true);
    if (event.key === 'ArrowRight') this.show(this.currentIndex + 1, true);
  }

  startTimer() {
    if (
      !this.autoplayEnabled
      || this.paused
      || this.timer
      || this.dragState
      || this.isSettling
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;
    this.timer = window.setInterval(() => this.show(this.currentIndex + 1), this.interval);
  }

  stopTimer() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartTimer() {
    this.stopTimer();
    if (!this.dragState && !this.isSettling && !this.pointerInside && !this.focusInside) this.startTimer();
  }

  toggleAutoplay() {
    this.paused = !this.paused;
    const button = this.querySelector('[data-fade-toggle]');
    button?.setAttribute('aria-pressed', String(this.paused));
    button?.querySelector('[data-pause-icon]')?.toggleAttribute('hidden', this.paused);
    button?.querySelector('[data-play-icon]')?.toggleAttribute('hidden', !this.paused);
    button?.querySelector('[data-pause-label]')?.toggleAttribute('hidden', this.paused);
    button?.querySelector('[data-play-label]')?.toggleAttribute('hidden', !this.paused);
    this.paused ? this.stopTimer() : this.restartTimer();
  }
}

if (!customElements.get('fade-hero-carousel')) {
  customElements.define('fade-hero-carousel', FadeHeroCarousel);
}